import cn from 'classnames';
import { useRouter } from 'next/router';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon } from 'components';
import Calendar from 'components/Calendar';
import { ButtonV3 } from 'components/ComponentV2';
import { Row } from 'components/layout';

import { useCommon } from 'hooks/useCommon';

import { EventTouchpointable } from 'lib/models/event';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseResponse } from 'lib/utils/parser';

import { getDayDateAndTime } from 'utils/time';
import NewCountBadge from '../NewCountBadge';
import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from './Sidebar.module.scss';

interface SidebarProps {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
}
export const Sidebar: React.FC<SidebarProps> = ({
  selectedTab,
  setSelectedTab,
}): ReactElement => {
  const router = useRouter();

  const { onShare } = useCommon();

  const { id } = router.query;

  const { touchpoint, touchpointDashboard } = useAttendeesFilter();
  const {
    touchpointable,
    cohost_application,
    title,
    short_slug,
    creatable_for,
  } = touchpoint || {};
  const isHost = cohost_application ? false : true;
  const { unseen_waitlist_count } = touchpointDashboard || {};
  const isSocietyEvent = useMemo(() => {
    return creatable_for?.data.type === 'student_society';
  }, [creatable_for]);

  const [t] = useTranslation('event-dashboard');
  const editLink = useMemo(() => {
    return `${(isSocietyEvent
      ? PAGE_ROUTES.EVENTS_EDIT
      : PAGE_ROUTES.CAMPAIGNS_EDIT
    ).replace('[id]', id as string)}?step=1&type=${TouchpointType.Event}`;
  }, [id, isSocietyEvent]);

  const getEventLocation = useMemo(() => {
    if (touchpoint) {
      const { is_online, online_link, place } = touchpoint;
      if (is_online) {
        if (online_link) {
          return (
            <div className={styles.location}>
              <div className={styles.iconBox}>
                <Icon iconName={'icon_link'} />
              </div>
              <div>
                <p>
                  <a href={online_link} target="_blank" rel="noreferrer">
                    {online_link}
                  </a>
                </p>
              </div>
            </div>
          );
        } else if (isHost) {
          return (
            <ButtonV3
              variant="contained"
              color="secondary"
              onClick={() => {
                router.push(editLink);
              }}
              startIcon="icon_plus"
            >
              {t('sidebar.add-meeting-link')}
            </ButtonV3>
          );
        }
      } else {
        if (place.data) {
          const { address } = parseResponse(place);

          return (
            <div className={styles.location}>
              <div className={styles.iconBox}>
                <Icon iconName={'icon_pin'} />
              </div>
              <div>
                <p>{address}</p>
              </div>
            </div>
          );
        } else if (isHost) {
          return (
            <ButtonV3
              variant="contained"
              color="secondary"
              onClick={() => {
                router.push(editLink);
              }}
              startIcon="icon_plus"
            >
              {t('sidebar.add-event-location')}
            </ButtonV3>
          );
        }
      }
    }
  }, [touchpoint, id, router]);

  const getDates = useMemo(() => {
    if (touchpointable) {
      const { start_date, end_date } = parseResponse(
        touchpointable as EventTouchpointable
      );
      const calenderDate = new Date(start_date);
      const startDate = getDayDateAndTime(calenderDate);
      if (end_date) {
        const endDate = getDayDateAndTime(new Date(end_date));
        return (
          <div className={styles.date}>
            <Calendar startDate={calenderDate} className={styles.calender} />
            <div>
              {startDate.day === endDate.day &&
              startDate.month === endDate.month &&
              startDate.year === endDate.year ? (
                <>
                  {startDate.dayOfWeek}, {startDate.month} {startDate.day}{' '}
                  <br />
                  {startDate.timeAMPM} - {endDate.timeAMPM}
                </>
              ) : (
                <>
                  {startDate.month} {startDate.day} {startDate.timeAMPM} -{' '}
                  {endDate.month} {endDate.day} {endDate.timeAMPM}
                </>
              )}
            </div>
          </div>
        );
      }

      return (
        <div className={styles.date}>
          <Calendar startDate={calenderDate} className={styles.calender} />
          <div>
            {startDate.dayOfWeek}, {startDate.month} {startDate.day} <br />
            {startDate.timeAMPM}
          </div>
        </div>
      );
    }
  }, [touchpointable]);

  const getShareButton = useMemo(() => {
    if (touchpointable) {
      const { is_online, online_link, place } = touchpoint;
      if ((is_online && online_link) || (!is_online && place.data)) {
        return (
          <div className={styles.actionSection}>
            <ButtonV3
              color="secondary"
              size="small"
              onClick={() => {
                onShare(title, `${window.location.origin}/${short_slug}`);
              }}
              startIcon="icon_link"
              className={cn({ [styles.fullWidth]: !isHost })}
            >
              {t('common.share')}
            </ButtonV3>
            {isHost && (
              <ButtonV3
                color="secondary"
                size="small"
                onClick={() => router.push(editLink)}
                startIcon="icon_pencil"
              >
                {t('common.edit')}
              </ButtonV3>
            )}
          </div>
        );
      }
    }
  }, [touchpointable]);

  return (
    <section className={styles.sidebar}>
      <div className={styles.menuList}>
        <Row
          columnGap={10}
          className={cn(styles.menuListItem, {
            [styles.active]: selectedTab === 'overview',
          })}
          onClick={() => {
            setSelectedTab('overview');
            router.push(
              router.pathname.replace('[id]', id as string) + '?tab=overview',
              undefined,
              {
                shallow: true,
              }
            );
          }}
        >
          <Icon
            iconName="icon_layout-11"
            className={cn(styles.menuListItemIcon, {
              [styles.active]: selectedTab === 'overview',
            })}
          />
          <p
            className={cn(styles.menuListItemText, {
              [styles.active]: selectedTab === 'overview',
            })}
          >
            {t('sidebar.overview')}
          </p>
        </Row>
        <Row
          columnGap={10}
          className={cn(styles.menuListItem, {
            [styles.active]: selectedTab === 'hosts',
          })}
          onClick={() => {
            setSelectedTab('hosts');
            router.push(
              router.pathname.replace('[id]', id as string) + '?tab=hosts',
              undefined,
              {
                shallow: true,
              }
            );
          }}
        >
          <Icon
            iconName={
              selectedTab === 'hosts' ? 'icon_badge-filled' : 'icon_badge'
            }
            className={cn(styles.menuListItemIcon, {
              [styles.active]: selectedTab === 'hosts',
            })}
          />
          <p
            className={cn(styles.menuListItemText, {
              [styles.active]: selectedTab === 'hosts',
            })}
          >
            {t('sidebar.hosts')}
          </p>
        </Row>
        <Row
          columnGap={10}
          className={cn(styles.menuListItem, {
            [styles.active]: selectedTab === 'guests',
          })}
          onClick={() => {
            setSelectedTab('guests');
            router.push(
              router.pathname.replace('[id]', id as string) + '?tab=guests',
              undefined,
              {
                shallow: true,
              }
            );
          }}
        >
          <Icon
            iconName={
              selectedTab === 'guests'
                ? 'icon_users-wm-fill-regular-small-fill'
                : 'icon_users-wm-fill-regular-small'
            }
            className={cn(styles.menuListItemIcon, {
              [styles.active]: selectedTab === 'guests',
            })}
          />
          <p
            className={cn(styles.menuListItemText, {
              [styles.active]: selectedTab === 'guests',
            })}
          >
            {t('sidebar.guests')}{' '}
          </p>
          {unseen_waitlist_count ? (
            <NewCountBadge count={unseen_waitlist_count} />
          ) : (
            <></>
          )}
        </Row>
        <Row
          columnGap={10}
          className={cn(styles.menuListItem, {
            [styles.active]: selectedTab === 'feedback',
          })}
          onClick={() => {
            setSelectedTab('feedback');
            router.push(
              router.pathname.replace('[id]', id as string) + '?tab=feedback',
              undefined,
              {
                shallow: true,
              }
            );
          }}
        >
          <Icon
            iconName={
              selectedTab === 'feedback' ? 'icon_star' : 'icon_star-outline'
            }
            className={cn(styles.menuListItemIcon, {
              [styles.active]: selectedTab === 'feedback',
            })}
          />
          <p
            className={cn(styles.menuListItemText, {
              [styles.active]: selectedTab === 'feedback',
            })}
          >
            {t('sidebar.feedback')}
          </p>
        </Row>
      </div>

      <div className={styles.infoSection}>
        <div className={styles.information}>
          {getDates}
          {getEventLocation}
        </div>
        {getShareButton}
      </div>
    </section>
  );
};
